export interface AppUser {
  uid: string;
  email: string;
  displayName: string;
  isPlatformAdmin: boolean;
}

export interface CurrentUser {
  uid: string;
  email: string;
  displayName: string;
  isPlatformAdmin: boolean;
  photoBase64?: string | null;
  phone?: string | null;
  providerIds: string[];
  onboardingCompletedAt?: string | null;
  onboardingStep?: number | null;
}

export type OrgCurrency = 'USD' | 'EUR' | 'GBP';
export type OrgUnits = 'imperial' | 'metric';
export type TravelTimeRate = 'one_hour_standard' | 'portal_to_portal';

/** How line items are presented on client-facing quote versions. */
import type { ItemizationPreference, LockedMaterialPrice } from '@iomi/shared';
export type { ItemizationPreference } from '@iomi/shared';

export interface OrgStripeData {
  customerId: string;
  subscriptionId: string;
  status: string;
  subscriptionQuantity: number;
  priceId: string;
  productId?: string;
  productName?: string;
  priceNickname?: string;
  cancelAtPeriodEnd: boolean;
  trialEnd?: number | null;
}

export type StripeConnectStatus =
  | 'not_started'
  | 'onboarding'
  | 'pending_verification'
  | 'active'
  | 'restricted'
  | 'disabled';

export interface OrgStripeConnectData {
  accountId: string;
  status: StripeConnectStatus;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  onboardingCompletedAt?: string;
  createdAt: string;
}

export interface OrgSubscriptionCost {
  unitAmount: number;
  totalAmount: number;
  currency: string;
  interval: string;
  nickname: string | null;
}

export type QuoteNumberSeparator = '-' | '.' | ' ' | '';
export type QuoteNumberDigitCount = 3 | 4 | 5 | 6;
export type QuoteNumberMethod = 'sequential' | 'year-sequential' | 'random' | 'date-based';

export interface QuoteNumberConfig {
  prefix: string;
  separator: QuoteNumberSeparator;
  digitCount: QuoteNumberDigitCount;
  startingNumber: number;
  method: QuoteNumberMethod;
}

export const QUOTE_NUMBER_CONFIG_DEFAULTS: QuoteNumberConfig = {
  prefix: 'QT',
  separator: '-',
  digitCount: 5,
  startingNumber: 1,
  method: 'sequential',
};

/** State mover license entry (state abbreviation + license number). */
export interface StateLicense {
  state: string;
  licenseNumber: string;
}

export type CommercialBallparkMethod = 'per_employee' | 'per_sqft';

export interface PaymentScheduleDefaults {
  collectDeposit: boolean;
  depositType: 'fixed' | 'percent';
  depositAmount?: number;
  balanceCollection?: 'before_move' | 'on_completion' | 'split';
  splitBeforeMovePercent?: number;
}

export interface BookingConfig {
  enabled: boolean;
  welcomeMessage?: string;
  enableLockInDate: boolean;
  enableRequestEstimate: boolean;
  /** Whether to show a price estimate to the customer on the booking form. Defaults to true. */
  enableShowPriceEstimate?: boolean;
  depositType?: 'fixed' | 'percent';
  depositAmount?: number;
  customDiscoverySources?: string[];
  /** Whether online quotes for customers are enabled (shareable link). */
  enableOnlineQuote?: boolean;
  /** Whether to accept payments via Stripe Connect for online quotes. Defaults to true when enableOnlineQuote is true. */
  enableAcceptPayments?: boolean;
  /** Primary brand color hex code for customer-facing pages. */
  brandPrimaryColor?: string;
  /** Title text color hex code — auto-selected for contrast, user-overridable. */
  brandTitleTextColor?: string;
}

export interface Organization {
  id: string;
  name: string;
  /** URL-safe slug used in the public booking URL (booking.iomipro.com/{slug}). Unique across all orgs. */
  slug?: string;
  logoBase64?: string;
  maxUsers?: number | null;
  emailDomain?: string;
  inviteEmail?: string;
  defaultCurrency?: OrgCurrency;
  defaultUnits?: OrgUnits;
  efficiencyMultiplier?: number;
  defaultTravelTimeRate?: TravelTimeRate;
  /** Fixed travel time hours when travel rate is 'one_hour_standard'. Defaults to 1. */
  fixedTravelTimeHours?: number;
  // ── Commercial-specific quote defaults ──
  commercialDefaultTravelTimeRate?: TravelTimeRate;
  commercialFixedTravelTimeHours?: number;
  commercialEfficiencyMultiplier?: number;
  commercialDefaultQuoteValidityDays?: number | null;
  /** Custom shift types for commercial quotes (name + length in hours). */
  commercialShiftSettings?: ShiftSetting[];
  // ── Residential-specific quote defaults ──
  residentialDefaultTravelTimeRate?: TravelTimeRate;
  residentialFixedTravelTimeHours?: number;
  residentialEfficiencyMultiplier?: number;
  residentialDefaultQuoteValidityDays?: number | null;
  /** Custom shift types for residential quotes (name + length in hours). */
  residentialShiftSettings?: ShiftSetting[];
  /** Residential pricing model: flat_rate or hourly_rate. Defaults to 'hourly_rate'. */
  residentialPricingModel?: 'flat_rate' | 'hourly_rate';
  billingEmail?: string;
  /** Legal company name shown on client-facing quotes (if different from org name). */
  legalName?: string;
  /** Physical business address. */
  businessAddress?: { address: string; placeId: string };
  /** Company phone number. */
  phone?: string;
  /** Company website URL. */
  website?: string;
  /** USDOT number (required for interstate moves in the U.S.). */
  usdotNumber?: string;
  /** MC number (Motor Carrier authority for interstate). */
  mcNumber?: string;
  /** State mover license numbers (multiple allowed, one per state). */
  stateLicenses?: StateLicense[];
  /** Footer text for generated quote PDFs. */
  quoteFooter?: string;
  quoteNumberConfig?: QuoteNumberConfig;
  nextQuoteNumber?: number;
  nextQuoteNumberByYear?: Record<string, number>;
  /** Default number of days a quote version stays valid. null = never expires. */
  defaultQuoteValidityDays?: number | null;
  /** When true, this org is used for demos and excluded from production stats. */
  isDemo?: boolean;
  /** Feature flags — all default to true (enabled) when undefined. */
  featureDispatch?: boolean;
  featureJobs?: boolean;
  featureClientPortal?: boolean;
  featureQuotePdfs?: boolean;
  /** Whether SmartMoving integration is enabled for this org. */
  featureSmartMovingIntegration?: boolean;
  /** Whether commercial moving quotes are enabled. Defaults to true for existing orgs. */
  featureCommercialMovingQuotes?: boolean;
  /** Whether residential moving quotes are enabled. Defaults to false for existing orgs. */
  featureResidentialMovingQuotes?: boolean;
  /** Whether the Lead Pipeline feature is enabled. Defaults to false. */
  featureLeadPipeline?: boolean;
  /** Whether Fleet Management is enabled. Defaults to false. */
  featureFleetManagement?: boolean;
  /** Whether Online Lead Capture (booking form) is enabled. Defaults to false. Requires featureLeadPipeline. */
  featureOnlineLeadCapture?: boolean;
  /** Whether Stripe Connect payment processing is enabled. Defaults to false. */
  featureStripeConnect?: boolean;
  /** Whether Customer Payments (offline + Stripe) is enabled. Defaults to false. */
  featureCustomerPayments?: boolean;
  /** Allowed features — controlled by IOMI admins. */
  allowedFeatureDispatch?: boolean;
  allowedFeatureJobs?: boolean;
  allowedFeatureClientPortal?: boolean;
  allowedFeatureQuotePdfs?: boolean;
  allowedFeatureSmartMovingIntegration?: boolean;
  allowedFeatureCommercialMovingQuotes?: boolean;
  allowedFeatureResidentialMovingQuotes?: boolean;
  allowedFeatureLeadPipeline?: boolean;
  allowedFeatureFleetManagement?: boolean;
  allowedFeatureOnlineLeadCapture?: boolean;
  allowedFeatureStripeConnect?: boolean;
  allowedFeatureCustomerPayments?: boolean;
  /** Whether to use a blended truck + driver rate instead of separate rates. */
  useBlendedTruckDriverRate?: boolean;
  /** Whether residential flat rates are enabled. */
  residentialFlatRatesEnabled?: boolean;
  /** Whether commercial ballpark estimates are enabled. */
  commercialBallparkEnabled?: boolean;
  /** Ballpark estimation method: per employee/workstation or per square foot. */
  commercialBallparkMethod?: CommercialBallparkMethod;
  /** The rate for the selected ballpark method. */
  commercialBallparkRate?: number;
  /** Optional estimate range percentage (e.g. 20 means +/- 20%). */
  commercialBallparkRangePercent?: number | null;
  /** Optional estimate range percentage for residential flat-rate estimates. */
  residentialBallparkRangePercent?: number | null;
  /** Itemization preference for commercial quote versions. Defaults to 'single_line'. */
  commercialItemizationPreference?: ItemizationPreference;
  /** Whether materials are itemized on commercial client-facing quotes. */
  commercialItemizeMaterials?: boolean;
  /** Whether scope of services are itemized on commercial client-facing quotes. */
  commercialItemizeScopeOfServices?: boolean;
  /** Itemization preference for residential quote versions. Defaults to 'single_line'. */
  residentialItemizationPreference?: ItemizationPreference;
  /** Whether materials are itemized on residential client-facing quotes. */
  residentialItemizeMaterials?: boolean;
  /** Whether scope of services are itemized on residential client-facing quotes. */
  residentialItemizeScopeOfServices?: boolean;
  /** Booking form configuration. */
  bookingConfig?: BookingConfig;
  stripe?: OrgStripeData;
  stripeConnect?: OrgStripeConnectData;
  /** Whether automatic tax calculation is enabled for this org. Defaults to false. */
  calculateTaxes?: boolean;
  /** Default tax rate used when automatic tax calculation is disabled (e.g. 0.05 for 5%). */
  defaultTaxRate?: number;
  /** Local move radius type — 'distance' (miles) or 'time' (hours). Defaults to 'distance'. */
  localMoveRadiusType?: 'distance' | 'time';
  /** Local move radius value — miles (default 50) or hours, depending on localMoveRadiusType. */
  localMoveRadiusValue?: number;
  /** Default payment schedule preferences. */
  paymentScheduleDefaults?: PaymentScheduleDefaults;
  subscriptionCost?: OrgSubscriptionCost;
  activeUserCount?: number;
  stripeStatus?: string;
  createdAt: string;
  updatedAt: string;
}

export type OrgRole = 'admin' | 'estimator' | 'mover' | 'dispatcher' | 'field_lead';

export interface OrgUser {
  uid: string;
  email: string;
  displayName: string;
  role: OrgRole;
  status?: 'active' | 'invited' | 'inactive';
  photoBase64?: string | null;
  /** Inactive record that never activated (un-accepted invite); removable outright. */
  neverActivated?: boolean;
}

export interface OrgMembership {
  orgId: string;
  orgName: string;
  role: OrgRole;
  /** Feature flags — all default to true (enabled) when undefined. */
  featureDispatch?: boolean;
  featureJobs?: boolean;
  featureClientPortal?: boolean;
  featureQuotePdfs?: boolean;
  /** Whether SmartMoving integration is enabled for this org. */
  featureSmartMovingIntegration?: boolean;
  /** Whether commercial moving quotes are enabled. Defaults to true. */
  featureCommercialMovingQuotes?: boolean;
  /** Whether residential moving quotes are enabled. Defaults to false. */
  featureResidentialMovingQuotes?: boolean;
  /** Whether the Lead Pipeline feature is enabled. Defaults to false. */
  featureLeadPipeline?: boolean;
  /** Whether Fleet Management is enabled. Defaults to false. */
  featureFleetManagement?: boolean;
  /** Whether Online Lead Capture (booking form) is enabled. Defaults to false. */
  featureOnlineLeadCapture?: boolean;
  /** Whether Customer Payments is enabled for this org. Defaults to false. */
  featureCustomerPayments?: boolean;
}

export type ResourceSlug = 'supervisor' | 'mover' | 'driver' | 'truck' | 'truck_driver';

export interface ResourceRate {
  slug: ResourceSlug;
  name: string;
  description: string;
  hourlyRate: number;
}

/** Per-quote crew rate entry — loaded once from org defaults; rates and quantities can be overridden per quote. */
export interface QuoteCrewMember {
  slug: ResourceSlug;
  name: string;
  hourlyRate: number;
  /** Per-resource headcount. Computed by the formula engine; overridable via the breakdown page. */
  quantity?: number;
}

// ── Residential Flat Rates ─────────────────────────────────────────────────

export interface ResidentialFlatRateRoom {
  bedroomCount: number;
  label: string;
  enabled: boolean;
  flatRate: number;
  crewSize?: number;
  trucks?: number;
  jobDurationHours?: number;
}

export interface ResidentialFlatRateAddon {
  slug: string;
  label: string;
  flatRate: number;
}

export interface ResidentialFlatRates {
  rooms: ResidentialFlatRateRoom[];
  addons: ResidentialFlatRateAddon[];
  specialtyItemsEnabled?: boolean;
}

export type WarehouseType = 'owned' | 'rented' | 'partner';
export type WarehouseStatus = 'active' | 'inactive';

export interface Warehouse {
  id: string;
  name: string;
  address: string;
  placeId: string;
  note: string;
  type?: WarehouseType;
  status?: WarehouseStatus;
  createdAt: string;
  updatedAt: string;
}

/** @deprecated Use dynamic fleet default truck types instead. Kept for backward compatibility with legacy data. */
export type TruckType = 'cargo_van' | 'sprinter_van' | '12ft_box' | '16ft_box' | '20ft_box' | '24ft_box' | '26ft_box' | 'flatbed' | 'other';
export type TruckStatus = 'active' | 'maintenance' | 'inactive';
export type TruckFuelType = 'gas' | 'diesel' | 'electric' | 'hybrid';

export interface Truck {
  id: string;
  name: string;
  /** Truck type — can be a legacy TruckType enum value or a fleet default truck type ID/name. */
  type?: string;
  year?: number;
  make?: string;
  model?: string;
  licensePlate?: string;
  capacity?: number;
  weightLimit?: number;
  /** Warehouse ID, or special values 'on_job' / 'shop'. */
  currentLocation?: string;
  status?: TruckStatus;
  fuelType?: TruckFuelType;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AddEquipmentEntry {
  itemId: string;
  itemName: string;
  quantity: number;
}

export interface QuoteFormula {
  originOptimumCrewSize: number;
  destinationOptimumCrewSize: number;
  combinedOptimumCrewSize: number;
  totalKZ: number;
  truckloads: number;
  /** Man-hours from KZ-based truckloads for Casegoods items only. */
  casegoodsManHours?: number;
  /** Man-hours from KZ-based truckloads for Modular items before disassembly/reassembly adjustments. */
  modularBaseManHours?: number;
  /** Modular man-hours after disassembly (×0.05) and reassembly (×0.3) adjustments. */
  modularManHours?: number;
  /** Man-hours for modular workstation disassembly: modularBaseManHours × 0.05. */
  modularDisassemblyManHours?: number;
  /** Man-hours for modular workstation reassembly: modularBaseManHours × 0.30. */
  modularReassemblyManHours?: number;
  /** casegoodsManHours + modularManHours + additionalManHours from inventory. Pre-multiplier. */
  baseManHours?: number;
  efficiencyMultiplier: number;
  /** baseManHours × efficiencyMultiplier */
  manHours: number;
  approxCrewSize: number;
  finalCrewSize: number;
  actualJobDuration: number;
  method: 'Shuttle' | 'Load & Go' | 'Internal';
  truckQuantity: number;
  /** Number of drivers. For Load & Go = truckQuantity. For Shuttle, derived from transit time table. */
  driverQuantity?: number;
  travelTimeToJobHours: number;
  finalTransitTimeHours: number;
  totalHours: number;
  totalHourlyRate: number;
  finalCostEstimate: number;
  /** Materials (packing items + equipment) cost for the move's rental length. Included in finalCostEstimate. The estimator can override it; a full recalculation resets it. */
  materialsCost?: number;
  /** Total number of materials (sum of quantities) behind materialsCost. */
  materialsCount?: number;
  // ── Residential formula fields ─────────────────────────────────────────
  /** Total cubic feet from all residential room items. */
  totalCubicFeet?: number;
  /** Total weight (lbs) from all residential room items. */
  totalWeight?: number;
  /** Total move units from all residential room items. */
  totalMoveUnits?: number;
  /** Extra hours added for bulky items. */
  bulkyItemHours?: number;
  /** Extra hours added for disassembly/reassembly items. */
  disassemblyHours?: number;
  /** Sum of labor points (moveUnits + disassembly penalty + bulky penalty) for crew tier lookup. */
  totalLaborPoints?: number;
  /** Crew size from tier lookup, before access bump. */
  crewSizeTier?: number;
  /** Whether the +1 crew access bump was applied (3+ stories, no elevator). */
  accessBumpApplied?: boolean;
  /** Sum of item load minutes (BASE_MINUTES + disassembly + bulky per item). */
  totalItemMinutes?: number;
  /** totalItemMinutes / crewSize — parallel work. */
  baseLoadMinutes?: number;
  /** Origin flights (stories - 1). */
  originFlights?: number;
  /** Computed origin access multiplier (stairs/elevator + long carry). */
  originAccessMultiplier?: number;
  /** Load minutes at origin (baseLoadMinutes × originMultiplier). */
  loadMinutes?: number;
  /** Destination flights (stories - 1). */
  destFlights?: number;
  /** Computed destination access multiplier (stairs/elevator + long carry). */
  destAccessMultiplier?: number;
  /** Unload minutes at destination (baseLoadMinutes × UNLOAD_FACTOR × destMultiplier). */
  unloadMinutes?: number;
  /** Drive time in minutes (from transit data). */
  driveMinutes?: number;
  /** Flat overhead buffer in minutes. */
  overheadMinutes?: number;
  /** Sum of all time components in minutes. */
  totalMinutes?: number;
  /** Number of bulky items (quantity-weighted). */
  bulkyItemCount?: number;
  /** Number of items requiring disassembly (quantity-weighted). */
  disassemblyItemCount?: number;
  /** Usable truck volume after load efficiency (TRUCK_CAPACITY_CF × LOAD_EFFICIENCY). */
  usableCubicFeet?: number;
  /** Trucks needed by volume: ceil(totalCubicFeet / usableCubicFeet). */
  trucksByVolume?: number;
  /** Trucks needed by weight: ceil(totalWeight / TRUCK_WEIGHT_LIMIT). */
  trucksByWeight?: number;
  /** Recommended truck types and counts based on volume/weight fit. */
  recommendedTruckTypes?: Array<{ type: string; count: number; capacityPerTruck: number }>;
  /** Extra man-hours for building protection installation when truckloads > 6. */
  buildingProtectionManHours?: number;
  // ── Residential flat-rate formula fields ──────────────────────────────────
  /** Whether this formula was computed using the flat-rate path. */
  isFlatRate?: boolean;
  /** Base flat-rate price for the selected room count (before add-ons). */
  flatRateRoomPrice?: number;
  /** Label for the matched room count tier (e.g. "2 Bedrooms"). */
  flatRateRoomLabel?: string;
  /** Bedroom count that matched the flat-rate tier. */
  flatRateBedroomCount?: number;
  /** Add-on line items applied to the flat-rate quote. */
  flatRateAddons?: Array<{ slug: string; label: string; amount: number }>;
  /** Total flat-rate price (room price + sum of add-ons). */
  flatRateTotal?: number;
  calculatedAt: string;
  /**
   * Fingerprint of the formula inputs at the time this formula was computed.
   * Used by useFormulaEngine to skip re-computation when inputs haven't changed,
   * so manual adjustments on the breakdown page survive navigation.
   */
  formulaInputsFingerprint?: string;
}

export interface WarehouseTransitLeg {
  distanceMeters: number;
  durationSeconds: number;
  calculatedAt: string;
}

export interface TravelToJob {
  originWarehouseId?: string;
  destinationWarehouseId?: string;
  originTravelSeconds?: number;
  destinationTravelSeconds?: number;
}

export type QuoteType = 'commercial' | 'residential';
export type ResidentialItemType = 'customer_item' | 'packing_item' | 'equipment';

export interface MasterInventoryItem {
  id: string;
  name: string;
  type: 'Casegoods' | 'Modular Component' | 'Modular Equipment';
  kz: number;
  description?: string;
  notes?: string;
  imageBase64?: string;
  additionalManHours?: number;
  addEquipment?: AddEquipmentEntry[];
  /** Which quote type this item belongs to. Defaults to 'commercial' when undefined. */
  quoteType?: QuoteType;
  // ── Accessorial service fields (commercial only) ────────────────────────
  /** Flag: this item has an associated accessorial service. Mutually exclusive with additionalManHours. */
  isAccessorial?: boolean;
  /** Label for the accessorial service (e.g., "Glass Bonding"). Required when isAccessorial is true. */
  accessorialLabel?: string;
  /** Man-hours at origin for the accessorial service. */
  accessorialOriginManHours?: number;
  /** Man-hours at destination for the accessorial service. */
  accessorialDestinationManHours?: number;
  // ── Residential-specific fields ─────────────────────────────────────────
  photo?: string;
  moveUnits?: number;
  cubicFeet?: number;
  weightInPounds?: number;
  bulky?: boolean;
  requiredDisassembly?: boolean;
  residentialType?: ResidentialItemType;
  /** Room ID reference — links to a master or org residential room. Residential items only. */
  roomId?: string;
  /** Price per unit in dollars. Packing items and equipment only. */
  unitCost?: number;
  /** Rental length in days. Blank means the item is purchased/consumed rather than returned. */
  rentalDays?: number;
  createdAt: string;
  updatedAt: string;
}

export interface MasterServiceItem {
  id: string;
  name: string;
  /** Optional short code / abbreviation for the service (e.g. "A1", "MO2"). */
  code?: string;
  party: 'Customer' | 'Mover';
  note?: string;
  /** Ordering index within the party group. Not displayed in UI. */
  order: number;
  /** Which quote type this service belongs to. Defaults to 'commercial' when undefined. */
  quoteType?: QuoteType;
  createdAt: string;
  updatedAt: string;
}

export interface OrgInventoryItem extends MasterInventoryItem {
  /**
   * Set by the list endpoint, which omits `imageBase64`/`photo` to keep the
   * payload small. True when the item has an image to fetch lazily via
   * GET /org-admin/:orgId/inventory/:itemId/image.
   */
  hasImage?: boolean;
  /** 'system' = copied from master; 'org' = created by the org itself */
  source: 'system' | 'org';
  /** ID of the original master item (for system-sourced items) */
  masterItemId?: string;
}

export interface OrgServiceItem extends MasterServiceItem {
  /** 'system' = copied from master; 'org' = created by the org itself */
  source: 'system' | 'org';
  /** ID of the original master item (for system-sourced items) */
  masterItemId?: string;
}

// ── Residential Rooms ────────────────────────────────────────────────────────

export type RoomProfileLevel = 'minimal' | 'standard' | 'full' | 'heavy';

export interface RoomProfileItem {
  itemId: string;
  itemName: string;
  quantity: number;
}

export interface RoomProfile {
  level: RoomProfileLevel;
  averageSquareFeet?: number;
  items: RoomProfileItem[];
}

export interface MasterResidentialRoom {
  id: string;
  type: 'residential';
  name: string;
  sortOrder?: number;
  profiles: RoomProfile[];
  createdAt: string;
  updatedAt: string;
}

export interface OrgResidentialRoom extends MasterResidentialRoom {
  source: 'system' | 'org';
  masterItemId?: string;
}

// ── SmartMoving Integration ──────────────────────────────────────────────

export interface SmartMovingConfig {
  enabled: boolean;
  apiKey: string;
  providerKey?: string;
  baseUrl?: string;
  syncEnabled: boolean;
  syncIntervalMinutes: number;
  lastSyncAt: string | null;
  lastSyncStatus: 'success' | 'error' | null;
  lastSyncError: string | null;
  syncFilters: {
    serviceTypes: string[];
    statuses: string[];
  };
  fieldMapping: Record<string, string>;
  pushBackEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SyncLogEntry {
  id: string;
  timestamp: string;
  direction: 'inbound' | 'outbound';
  action: 'import' | 'update' | 'push';
  smartMovingId: string;
  iomiQuoteId?: string;
  status: 'success' | 'error' | 'skipped';
  details: string;
  error?: string;
}

// ── Quotes ──────────────────────────────────────────────────────────────────

export type LifecycleStage = 'lead' | 'quote' | 'job';

export const LIFECYCLE_STAGE_LABEL: Record<LifecycleStage, string> = {
  lead: 'Lead',
  quote: 'Quote',
  job: 'Job',
};

export type LeadStatus = 'new' | 'contacted' | 'estimate_scheduled' | 'not_pursuing' | 'lost';

export const LEAD_STATUS_LABEL: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  estimate_scheduled: 'Estimate Scheduled',
  not_pursuing: 'Not Pursuing',
  lost: 'Lost',
};

export const LEAD_STATUS_CLASSES: Record<LeadStatus, string> = {
  new: 'bg-blue-50 text-blue-700',
  contacted: 'bg-amber-50 text-amber-700',
  estimate_scheduled: 'bg-emerald-50 text-emerald-700',
  not_pursuing: 'bg-stone text-slate',
  lost: 'bg-rose/10 text-rose',
};

export type LeadSource =
  | 'google'
  | 'yelp'
  | 'homeadvisor_angi'
  | 'thumbtack'
  | 'nextdoor'
  | 'website_form'
  | 'repeat_customer'
  | 'referral'
  | 'social_media'
  | 'trade_show_event'
  | 'phone_call_inbound'
  | 'partner_broker'
  | 'other';

export const LEAD_SOURCE_LABEL: Record<LeadSource, string> = {
  google: 'Google',
  yelp: 'Yelp',
  homeadvisor_angi: 'HomeAdvisor / Angi',
  thumbtack: 'Thumbtack',
  nextdoor: 'Nextdoor',
  website_form: 'Website Form',
  repeat_customer: 'Repeat Customer',
  referral: 'Referral',
  social_media: 'Social Media',
  trade_show_event: 'Trade Show / Event',
  phone_call_inbound: 'Phone Call (Inbound)',
  partner_broker: 'Partner / Broker',
  other: 'Other',
};

// ── Inbound Channel + Discovery Source (replaces LeadSource for new leads) ──

/** How the lead entered the system — internal use, not customer-facing. */
export type InboundChannel =
  | 'booking_form'
  | 'website_form'
  | 'phone_call_inbound'
  | 'walk_in'
  | 'partner_broker'
  | 'manual_entry';

export const INBOUND_CHANNEL_LABEL: Record<InboundChannel, string> = {
  booking_form: 'Online Booking Form',
  website_form: 'Website Form',
  phone_call_inbound: 'Phone Call (Inbound)',
  walk_in: 'Walk-in',
  partner_broker: 'Partner / Broker',
  manual_entry: 'Manual Entry',
};

/** How the customer heard about the company — customer-facing. */
export type DiscoverySource =
  | 'google'
  | 'yelp'
  | 'homeadvisor_angi'
  | 'thumbtack'
  | 'nextdoor'
  | 'social_media'
  | 'repeat_customer'
  | 'referral'
  | 'trade_show_event'
  | 'other';

export const DISCOVERY_SOURCE_LABEL: Record<DiscoverySource, string> = {
  google: 'Google',
  yelp: 'Yelp',
  homeadvisor_angi: 'HomeAdvisor / Angi',
  thumbtack: 'Thumbtack',
  nextdoor: 'Nextdoor',
  social_media: 'Social Media',
  repeat_customer: 'Repeat Customer',
  referral: 'Referral',
  trade_show_event: 'Trade Show / Event',
  other: 'Other',
};

export type QuoteStatus = 'draft' | 'sent' | 'response_received' | 'booked' | 'declined' | 'cancelled';

export type JobStatus = 'scheduled' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';

export const JOB_STATUS_LABEL: Record<JobStatus, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  on_hold: 'On Hold',
  cancelled: 'Cancelled',
};

export const JOB_STATUS_CLASSES: Record<JobStatus, string> = {
  scheduled: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  completed: 'bg-emerald-50 text-emerald-700',
  on_hold: 'bg-stone text-slate',
  cancelled: 'bg-rose/10 text-rose',
};

// ── Job Stages (in-progress sub-stages) ──────────────────────────────────────

export type JobStage =
  | 'scheduled'
  | 'en_route_origin'
  | 'loading'
  | 'en_route_destination'
  | 'unloading'
  | 'customer_sign_off'
  | 'pending_return'
  | 'complete';

export const JOB_STAGES: JobStage[] = [
  'scheduled',
  'en_route_origin',
  'loading',
  'en_route_destination',
  'unloading',
  'customer_sign_off',
  'pending_return',
  'complete',
];

export const JOB_STAGE_LABEL: Record<JobStage, string> = {
  scheduled: 'Scheduled',
  en_route_origin: 'En Route to Origin',
  loading: 'Loading',
  en_route_destination: 'En Route to Destination',
  unloading: 'Unloading',
  customer_sign_off: 'Customer Sign-Off',
  pending_return: 'Pending Return',
  complete: 'Complete',
};

/** The primary action button label for each stage (null = no action). */
export const JOB_STAGE_ACTION: Record<JobStage, string | null> = {
  scheduled: null,
  en_route_origin: 'Arrived at Origin',
  loading: 'Loading Complete',
  en_route_destination: 'Arrived at Destination',
  unloading: 'Unloading Complete',
  customer_sign_off: 'Get Customer Sign-Off',
  pending_return: null,
  complete: null,
};

export const QUOTE_STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  response_received: 'Response Received',
  booked: 'Booked',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

/** Tailwind class pairs [bg, text] for each status. */
export const QUOTE_STATUS_CLASSES: Record<QuoteStatus, string> = {
  draft: 'bg-stone text-slate',
  sent: 'bg-plum/10 text-plum',
  response_received: 'bg-sky-50 text-sky-700',
  booked: 'bg-emerald-50 text-emerald-700',
  declined: 'bg-amber-50 text-amber-700',
  cancelled: 'bg-rose/10 text-rose',
};

export type ClientResponseType = 'accepted' | 'changes_requested' | 'declined';

export const CLIENT_RESPONSE_LABEL: Record<ClientResponseType, string> = {
  accepted: 'Client Accepted',
  changes_requested: 'Changes Requested',
  declined: 'Client Declined',
};

export const CLIENT_RESPONSE_CLASSES: Record<ClientResponseType, string> = {
  accepted: 'bg-emerald-50 text-emerald-700',
  changes_requested: 'bg-amber-50 text-amber-700',
  declined: 'bg-rose/10 text-rose',
};

export interface ClientResponse {
  type: ClientResponseType;
  message?: string;
  respondedAt: string;
}

export interface LatestClientResponse {
  type: ClientResponseType;
  versionId: string;
  respondedAt: string;
};

export type MoveTiming = 'Work week - day' | 'Work week - evening' | 'Weekend';

export const SHIFT_LENGTH_HOURS: Record<MoveTiming, number> = {
  'Work week - day': 7,
  'Work week - evening': 5,
  'Weekend': 8,
};

/** An org-configurable shift type with a custom name and length in hours. */
export interface ShiftSetting {
  name: string;
  lengthHours: number;
  /** Whether this shift is the default selection for new quotes. */
  isDefault?: boolean;
}

/** Default shift settings seeded for new organizations. */
export const DEFAULT_SHIFT_SETTINGS: ShiftSetting[] = [
  { name: 'Work week - day', lengthHours: 7, isDefault: true },
  { name: 'Work week - evening', lengthHours: 5 },
  { name: 'Weekend', lengthHours: 8 },
];

export type WaypointType = 'Truck' | 'Stairs' | 'Elevator';

export interface LogisticsData {
  /** Strides from start-node to waypoint (or directly to end-node if waypoint = Truck) */
  strides1?: number;
  distanceFeet1?: number;
  /** The intermediate waypoint. 'Truck' = no intermediate stop (walk directly to end). */
  waypoint?: WaypointType;
  /** Number of floor stories traversed via stairs. Only relevant when waypoint = 'Stairs'. */
  stories?: number;
  /** Strides from waypoint to end-node. Only relevant when waypoint != 'Truck'. */
  strides2?: number;
  distanceFeet2?: number;
  /** Whether a truck-height loading dock is available at this location. */
  loadingDock?: boolean;
}

export interface ModularWorkstationSample {
  itemId: string;
  itemName: string;
  quantitySampled: number;
}

export interface ModularWorkstations {
  totalWorkstations: number;
  moverDisassembling: boolean;
  moverReassembling: boolean;
  workstationsSampled: number;
  samples: ModularWorkstationSample[];
}

export interface QuoteServiceItem {
  serviceId: string;
  serviceName: string;
  /** Optional short code / abbreviation for the service (e.g. "A1", "MO2"). */
  code?: string;
  party: 'Customer' | 'Mover';
  order: number;
  enabled: boolean;
  /** When true, this service was added at the quote level (not from org services). */
  isCustom?: boolean;
  /** Optional note for the service. */
  note?: string;
}

export type ResidentialPropertyType =
  | 'single_family'
  | 'condo'
  | 'townhouse'
  | 'apartment'
  | 'manufactured'
  | 'multi_family';

export interface ResidentialPropertyDetails {
  propertyType?: ResidentialPropertyType;
  bedrooms?: number;
  bathrooms?: number;
  basement?: boolean;
  squareFootage?: number;
  stories?: number;
  elevator?: boolean;
  garageSpaces?: number;
  totalRoomCount?: number;
  pool?: boolean;
  /** Truck cannot park close to the building entrance (long driveway, distant loading dock, etc.). */
  longCarry?: boolean;
}

export type SmartProfileLevel = 'minimal' | 'standard' | 'heavy' | 'full';

export interface SmartProfileThreshold {
  level: SmartProfileLevel;
  maxSqft?: number;
}

export interface SmartRoomProfileConfig {
  propertyType: ResidentialPropertyType;
  thresholds: SmartProfileThreshold[];
  updatedAt?: string;
}

// ── Room Auto-Add Rules ─────────────────────────────────────────────────────

/** Fields that can be tested in auto-add rule conditions */
export type AutoAddRuleField =
  | 'propertyType'
  | 'bedrooms'
  | 'bathrooms'
  | 'totalRoomCount'
  | 'squareFootage'
  | 'lotSize'
  | 'garageSpaces'
  | 'basement'
  | 'pool';

/** Operators for numeric fields */
export type AutoAddNumericOp = '>=' | '<=' | '>' | '<' | '==' | '!=';
/** Operators for boolean fields */
export type AutoAddBooleanOp = '==' | '!=';
/** Operators for string/enum fields */
export type AutoAddEnumOp = 'in' | 'not_in';

export interface AutoAddCondition {
  field: AutoAddRuleField;
  operator: AutoAddNumericOp | AutoAddBooleanOp | AutoAddEnumOp;
  /** Value to compare: number for numeric, boolean for boolean, string[] for enum */
  value: number | boolean | string[];
}

/** How multiple conditions combine */
export type AutoAddLogic = 'AND' | 'OR';

/** How the quantity for the room is determined */
export interface AutoAddQuantity {
  /** 'fixed' = always add N rooms; 'driven' = quantity tracks a property field */
  mode: 'fixed' | 'driven';
  /** Fixed count (when mode = 'fixed') */
  fixed?: number;
  /** Which field drives the count (when mode = 'driven') */
  drivenBy?: 'bedrooms' | 'bathrooms' | 'totalRoomCount' | 'garageSpaces';
  /** Offset to add/subtract from the driven field value */
  offset?: number;
  /** Minimum resulting quantity */
  min?: number;
  /** Maximum resulting quantity */
  max?: number;
}

export interface RoomAutoAddRule {
  /** 'always' = unconditionally add; 'conditional' = check conditions first */
  trigger: 'always' | 'conditional';
  /** Conditions to evaluate (ignored when trigger = 'always') */
  conditions: AutoAddCondition[];
  /** How conditions combine — AND or OR (default AND) */
  logic: AutoAddLogic;
  /** How many instances of this room to add */
  quantity: AutoAddQuantity;
}

export interface MasterRoomAutoAddConfig {
  roomId: string;
  roomName: string;
  /** Array of rules for this room. Empty array = no auto-add rules (default). */
  rules: RoomAutoAddRule[];
  updatedAt?: string;
}

export interface OrgRoomAutoAddConfig extends MasterRoomAutoAddConfig {
  source: 'system' | 'org';
  masterRuleId?: string;
}

export interface RentCastPropertyData {
  id?: string;
  formattedAddress?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  county?: string;
  propertyType?: string;
  bedrooms?: number;
  bathrooms?: number;
  squareFootage?: number;
  lotSize?: number;
  yearBuilt?: number;
  ownerNames?: string[];
  legalDescription?: string;
  features?: Record<string, unknown>;
}

export interface QuoteAddress {
  address: string;
  placeId: string;
  floor?: string;
  officeNumber?: string;
  apt?: string;
  residentialProperty?: ResidentialPropertyDetails;
  importedPropertyData?: RentCastPropertyData;
}

export interface QuoteTransit {
  distanceMeters: number;
  durationSeconds: number;
  calculatedAt: string;
}

export interface CasegoodsInventoryItem {
  itemId: string;
  itemName: string;
  quantity: number;
  /** Item category — shared across commercial and residential items. */
  itemCategory?: ResidentialItemType;
}

// ── Quote Residential Rooms ─────────────────────────────────────────────────

export interface QuoteResidentialRoomItem {
  itemId: string;
  itemName: string;
  quantity: number;
  cubicFeet?: number;
  weightInPounds?: number;
  moveUnits?: number;
  bulky?: boolean;
  requiredDisassembly?: boolean;
}

export type QuoteRoomProfileLevel = 'empty' | RoomProfileLevel;

export interface QuoteResidentialRoom {
  id: string;
  roomId: string;
  roomName: string;
  profileLevel: QuoteRoomProfileLevel;
  items: QuoteResidentialRoomItem[];
  notes?: string;
}

export interface QuoteVersionLineItem {
  name: string;
  rate: number;
  qty: number;
  total: number;
  /** 'materials' lines are a flat amount for N items, not rate × hours. */
  kind?: 'materials';
}

export interface QuoteVersionMaterial {
  itemName: string;
  quantity: number;
  /** Unit cost locked at version creation (per rental period when `rentalDays` is set). `null` = no cost. Absent on older versions. */
  unitCost?: number | null;
  /** Rental period in days; `null` for purchased / consumed items. */
  rentalDays?: number | null;
  /** Whole rental periods billed for the move's rental length. */
  periodsBilled?: number;
  /** quantity × unitCost × periodsBilled; `null` when the item has no cost. */
  lineTotal?: number | null;
}

/** One crew resource on a version: role, how many, and the rate each one bills. */
export interface QuoteVersionCrewMember {
  name: string;
  quantity: number;
  hourlyRate: number;
}

/** Crew & truck composition snapshotted on every version regardless of itemization preference. */
export interface QuoteVersionCrewBreakdown {
  members: QuoteVersionCrewMember[];
  onSiteHours: number;
  travelHours: number;
  transitHours: number;
  totalHours: number;
  totalHourlyRate: number;
  method: 'Shuttle' | 'Load & Go' | 'Internal';
  crewSize: number;
  trucks: number;
}

export interface QuoteVersionService {
  serviceName: string;
  /** Optional short code / abbreviation for the service (e.g. "A1", "MO2"). */
  code?: string;
  party: 'Customer' | 'Mover';
}

export interface QuoteVersion {
  id: string;
  quoteId: string;
  versionNumber: number;
  /** Full version ID: e.g. "QT-00001-1" */
  quoteVersionId: string;
  quoteDate: string;
  customerCompanyName: string;
  originAddress?: string;
  destinationAddress?: string;
  pointOfContactEmail?: string;
  preferredMoveDate?: string;
  packingDate?: string;
  moveDeadline?: string;
  bindingQuote?: boolean;
  lineItems: QuoteVersionLineItem[];
  totalQuotedCost: number;
  /** Itemization preference used when this version was generated. */
  itemizationPreference?: ItemizationPreference;
  /** Snapshot of packing-item materials (only present if itemize-materials is enabled for the quote type). */
  materials?: QuoteVersionMaterial[];
  /** Snapshot of enabled scope-of-service items (only present if itemize-scope is enabled for the quote type). */
  scopeOfServices?: QuoteVersionService[];
  /** Crew & truck composition snapshot. Absent on versions created before it was recorded. */
  crewBreakdown?: QuoteVersionCrewBreakdown;
  pdfUrl?: string;
  /** ISO timestamp when this version expires. Absent = never expires. */
  expiresAt?: string;
  /** The validity period (in days) used when this version was created. */
  validityDays?: number | null;
  /** Point of contact name snapshot. */
  pointOfContactName?: string;
  /** Billing info snapshot. */
  billingName?: string;
  billingAddressLine1?: string;
  billingAddressLine2?: string;
  billingCity?: string;
  billingState?: string;
  billingZipCode?: string;
  createdBy: string;
  createdAt: string;
  /** Unique access token for the client portal. */
  portalToken?: string;
  /** Full URL to the client-facing online quote (built by the backend). */
  portalUrl?: string;
  /** ISO timestamp when the client first viewed this version via the portal. */
  clientViewedAt?: string;
  /** Total number of portal views. */
  clientViewCount?: number;
  /** Client's response to this quote version. */
  clientResponse?: ClientResponse;
  // Tax snapshot fields
  subtotal?: number;
  taxState?: string;
  laborTaxRate?: number;
  /** Tax rate applied to materials; 0 when the governing state does not tax them. */
  materialsTaxRate?: number;
  taxAmount?: number;
  totalWithTax?: number;
  requiresManualTaxReview?: boolean;
}

// ── Quote version export packet (GET /quotes/:orgId/:quoteId/versions/:versionId/export) ──
// Types and renderers live in @iomi/shared (quoteExport.ts), mirrored by the backend.
export type {
  ExportCellKind,
  ExportColumn,
  ExportCell,
  ExportTable,
  ExportSectionKey,
  ExportSection,
  ExportCopyKey,
  QuoteVersionExportPacket,
} from '@iomi/shared';

/** Expiration status for a quote version. */
export type VersionExpirationStatus = 'active' | 'expiring_soon' | 'expired' | 'never';

/** Compute the expiration status of a quote version. */
export function getVersionExpirationStatus(version: QuoteVersion): VersionExpirationStatus {
  if (!version.expiresAt) return 'never';
  const now = new Date();
  const expiresAt = new Date(version.expiresAt);
  if (now >= expiresAt) return 'expired';
  const daysLeft = (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  if (daysLeft <= 7) return 'expiring_soon';
  return 'active';
}

/** Format expiration for display. */
export function formatExpiration(version: QuoteVersion): string {
  if (!version.expiresAt) return 'Never expires';
  const expiresAt = new Date(version.expiresAt);
  const now = new Date();
  if (now >= expiresAt) {
    return `Expired on ${expiresAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (daysLeft <= 7) {
    return `Expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`;
  }
  return `Valid until ${expiresAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

export interface Quote {
  id: string;
  orgId: string;
  /** Human-readable quote identifier, e.g. "QT-00001". Unique per org. */
  quoteNumber?: string;
  /** Quote type — 'commercial' (default) or 'residential'. */
  type?: QuoteType;
  customerCompanyName: string;
  quoteStatus: QuoteStatus;
  createdBy: string;
  createdByName?: string;
  /** UID of the current quote lead — resolved server-side from the quoteLeadUser reference. */
  quoteLeadUserId?: string;
  /** Display name of the current quote lead — resolved live from users/{uid}. */
  quoteLeadDisplayName?: string;
  createdAt: string;
  updatedAt: string;
  numberOfEmployees?: number;
  pointOfContact?: {
    name?: string;
    phone?: string;
    email?: string;
    notes?: string;
  };
  estimateVisitDate?: string;
  movingDate?: {
    preferredDate?: string;
    deadline?: string;
    timing?: MoveTiming;
    /** Snapshot of the selected shift type name (persisted so edits/deletes to org shifts don't affect past quotes). */
    shiftType?: string;
    /** Shift length in hours corresponding to the selected shift type. */
    shiftLengthHours?: number;
  };
  addresses?: {
    origin?: QuoteAddress;
    internalMove?: boolean;
    destination?: QuoteAddress;
    transit?: QuoteTransit;
  };
  casegoodsInventory?: CasegoodsInventoryItem[];
  residentialRooms?: QuoteResidentialRoom[];
  /** How many days this move rents its materials for (whole days, default 1). Items with a rental length are billed in whole periods against this. */
  materialsRentalDays?: number;
  /** Material prices locked into this quote by the backend (like `crew` for hourly rates); they win over the catalog's price. */
  materialPrices?: Record<string, LockedMaterialPrice>;
  modularWorkstations?: ModularWorkstations;
  scopeOfServices?: QuoteServiceItem[];
  originLogistics?: LogisticsData;
  destinationLogistics?: LogisticsData;
  formula?: QuoteFormula;
  /** Crew resource rates for this quote — loaded once from org defaults; rates can be overridden per quote. */
  crew?: QuoteCrewMember[];
  /** Transit times from each warehouse (by warehouseId) to origin and destination job sites. */
  warehouseTransit?: Record<string, { toOrigin?: WarehouseTransitLeg; toDestination?: WarehouseTransitLeg }>;
  /** Selected warehouse IDs and their recorded travel times for this quote. */
  travelToJob?: TravelToJob;
  /** Travel time rate method for this quote — defaults to org setting on first save. */
  travelTimeRate?: TravelTimeRate;
  /** Efficiency multiplier applied to man-hours — frozen from org setting on first formula run. */
  efficiencyMultiplier?: number;
  /** Free-form estimator notes. */
  notes?: string;
  /** Photo metadata — actual files stored in Firebase Storage. */
  photos?: QuotePhoto[];
  /** UID of the assigned Move Lead (mover role). Set when quote is closed. */
  moveLeadUserId?: string;
  /** Display name of the assigned Move Lead — resolved live from users/{uid}. */
  moveLeadDisplayName?: string;
  /** Job status — independent of quote status. Set when a Move Lead is assigned. */
  jobStatus?: JobStatus;
  /** Win/loss outcome — recorded when status changes to closed_won or closed_lost. */
  outcome?: QuoteOutcome;
  /** Denormalized latest client response from any quote version — used for list queries. */
  latestClientResponse?: LatestClientResponse;
  /** Packing date — optional separate day for packing before the move. ISO date string. */
  packingDate?: string | null;
  /** Accessorial services date — `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm` (time optional). Must be ≤ move date if set. */
  accessorialServicesDate?: string | null;
  /** Move type — auto-populated from addresses, overridable by user. */
  moveType?: 'local' | 'intrastate' | 'interstate';
  /** Whether this is a binding quote (guarantees price) or non-binding (estimate). */
  bindingQuote?: boolean;
  /** External CRM record ID (e.g., SmartMoving opportunity ID). Unique per org. */
  externalId?: string;
  /** Which system created the quote (e.g., "smartmoving", "manual"). */
  source?: string;
  /** Lifecycle stage — distinguishes leads, quotes, and jobs within the same data model. */
  lifecycleStage?: LifecycleStage;
  /** Lead status — only used when lifecycleStage === 'lead'. */
  leadStatus?: LeadStatus;
  /** Reason the lead was lost — only set when leadStatus === 'lost'. */
  leadLostReason?: string;
  /** Reason the lead is not being pursued — only set when leadStatus === 'not_pursuing'. */
  leadNotPursuingReason?: string;
  /** Optional free-text note for not-pursuing reason. */
  leadNotPursuingNote?: string;
  /** @deprecated Use inboundChannel + discoverySource instead. How the lead was acquired. */
  leadSource?: LeadSource;
  /** How the lead entered the system (e.g. booking form, manual entry, phone call). */
  inboundChannel?: InboundChannel;
  /** How the customer heard about the company (e.g. Google, referral, Yelp). Customer-facing. */
  discoverySource?: DiscoverySource;
  /** Who referred this lead — only relevant when discoverySource === 'referral' (or legacy leadSource === 'referral'). */
  referredBy?: string;
  /** Date when someone should follow up with the lead. ISO date string. */
  nextFollowUp?: string;
  /** Rough dollar estimate of the move value — helps prioritize leads. */
  estimatedValue?: number;
  /** Free text for vague move dates like "June" or "last week of October". */
  estimatedMoveDateRange?: string;
  /** Origin zip code — fallback when full address not available. */
  originZipCode?: string;
  /** Destination zip code — fallback when full address not available. */
  destinationZipCode?: string;
  /** Number of bedrooms — residential leads/quotes only. */
  numberOfBedrooms?: number;
  /** Square footage — residential leads/quotes only. */
  squareFootage?: number;
  /** Number of floors at origin. */
  originStories?: number;
  /** Number of floors at destination. */
  destinationStories?: number;
  /** Whether elevator access is available at origin. */
  originElevatorAccess?: boolean;
  /** Whether elevator access is available at destination. */
  destinationElevatorAccess?: boolean;
  /** Unique token for the public booking form to identify this lead (UUID). */
  bookingToken?: string;
  /** Customer's chosen action from the booking form. */
  bookingAction?: 'lock_in_date' | 'request_estimate';
  /** Whether the dates are flexible (from booking form). */
  flexibleDate?: boolean;
  /** Default crew size from residential flat rate room config. */
  flatRateCrewSize?: number;
  /** Default truck quantity from residential flat rate room config. */
  flatRateTrucks?: number;
  /** Default job duration (hours) from residential flat rate room config. */
  flatRateJobDurationHours?: number;
  /** Per-quote pricing model override. When set to 'hourly_rate', a flat-rate residential
   *  quote behaves as hourly-rate for this quote only (Rooms & Inventory becomes active). */
  pricingOverride?: 'hourly_rate';
  /** Whether customer indicated they have specialty items (from booking form). */
  hasSpecialtyItems?: boolean;
  /** Customer-provided details about their specialty items. */
  specialtyItemsDetails?: string;
  /** Lead communication log — user-created entries documenting interactions with a lead. */
  leadCommunicationLog?: LeadCommunicationEntry[];
  /** @deprecated Use leadCommunicationLog */
  leadActivityLog?: LeadCommunicationEntry[];
  /** Billing information for quotes and invoices. */
  billingInfo?: {
    sameAsPointOfContact?: boolean;
    name?: string;
    sameAsOriginAddress?: boolean;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  // Tax fields (populated when formula runs and org has tax settings)
  taxState?: string;
  laborTaxRate?: number;
  /** Tax rate applied to materials; 0 when the governing state does not tax them. */
  materialsTaxRate?: number;
  taxAmount?: number;
  requiresManualTaxReview?: boolean;
}

export interface QuoteOutcome {
  reason: string;
  note?: string;
  recordedAt: string;
  recordedBy: string;
}

export interface QuotePhoto {
  id: string;
  fileName: string;
  url: string;
  createdAt: string;
}

export type QuotePatch = Partial<Pick<Quote,
  | 'customerCompanyName'
  | 'numberOfEmployees'
  | 'pointOfContact'
  | 'estimateVisitDate'
  | 'movingDate'
  | 'addresses'
  | 'casegoodsInventory'
  | 'residentialRooms'
  | 'materialsRentalDays'
  | 'modularWorkstations'
  | 'scopeOfServices'
  | 'originLogistics'
  | 'destinationLogistics'
  | 'formula'
  | 'crew'
  | 'travelToJob'
  | 'travelTimeRate'
  | 'efficiencyMultiplier'
  | 'quoteStatus'
  | 'createdBy'
  | 'createdByName'
  | 'quoteLeadUserId'
  | 'notes'
  | 'moveLeadUserId'
  | 'jobStatus'
  | 'outcome'
  | 'packingDate'
  | 'accessorialServicesDate'
  | 'moveType'
  | 'bindingQuote'
  | 'lifecycleStage'
  | 'leadStatus'
  | 'leadLostReason'
  | 'leadNotPursuingReason'
  | 'leadNotPursuingNote'
  | 'leadSource'
  | 'inboundChannel'
  | 'discoverySource'
  | 'referredBy'
  | 'nextFollowUp'
  | 'estimatedValue'
  | 'estimatedMoveDateRange'
  | 'originZipCode'
  | 'destinationZipCode'
  | 'numberOfBedrooms'
  | 'squareFootage'
  | 'originStories'
  | 'destinationStories'
  | 'originElevatorAccess'
  | 'destinationElevatorAccess'
  | 'bookingToken'
  | 'bookingAction'
  | 'flexibleDate'
  | 'leadCommunicationLog'
  | 'leadActivityLog'
  | 'pricingOverride'
  | 'billingInfo'
>>;

// ── Quote History Log ──────────────────────────────────────────────────────

export type QuoteHistoryType =
  | 'created'
  | 'status_change'
  | 'lead_changed'
  | 'move_lead_changed'
  | 'version_created'
  | 'section_updated'
  | 'formula_calculated'
  | 'date_changed'
  | 'address_changed'
  | 'photos_added'
  | 'customer_replied'
  | 'job_status_changed'
  | 'stage_advanced'
  | 'stage_reverted'
  | 'signoff_captured'
  | 'signoff_waived'
  | 'crew_changed'
  | 'trucks_changed'
  | 'outcome_changed'
  | 'issue_logged'
  | 'payment_request_sent'
  | 'payment_logged'
  | 'hours_reconciled'
  | 'deleted'
  | 'photos_removed'
  | 'truck_checkout'
  | 'truck_checkin'
  | 'portal_viewed'
  | 'lead_activity_logged';

/** @deprecated Use QuoteHistoryType */
export type QuoteActivityType = QuoteHistoryType;

export interface QuoteHistoryEntry {
  id: string;
  type: QuoteHistoryType;
  description: string;
  userId: string;
  userDisplayName: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

/** @deprecated Use QuoteHistoryEntry */
export type QuoteActivityEntry = QuoteHistoryEntry;

// ── Lead Communication Log ──────────────────────────────────────────────────

export type LeadCommunicationType =
  | 'phone_call'
  | 'email'
  | 'text_message'
  | 'in_person'
  | 'voicemail'
  | 'follow_up_scheduled'
  | 'note'
  | 'other';

export const LEAD_COMMUNICATION_TYPE_LABEL: Record<LeadCommunicationType, string> = {
  phone_call: 'Phone Call',
  email: 'Email',
  text_message: 'Text Message',
  in_person: 'In Person',
  voicemail: 'Voicemail',
  follow_up_scheduled: 'Follow-Up Scheduled',
  note: 'Note',
  other: 'Other',
};

export interface LeadCommunicationEntry {
  id: string;
  type: LeadCommunicationType;
  note: string;
  createdAt: string;
  createdBy: string;
  createdByName: string;
}

/** @deprecated Use LeadCommunicationType */
export type LeadActivityType = LeadCommunicationType;
/** @deprecated Use LEAD_COMMUNICATION_TYPE_LABEL */
export const LEAD_ACTIVITY_TYPE_LABEL = LEAD_COMMUNICATION_TYPE_LABEL;
/** @deprecated Use LeadCommunicationEntry */
export type LeadActivityEntry = LeadCommunicationEntry;

/** Format distanceMeters into a human-readable string based on org units. */
export function formatDistance(meters: number, units: OrgUnits = 'imperial'): string {
  if (units === 'metric') {
    const km = meters / 1000;
    return `${km.toFixed(1)} km`;
  }
  const miles = meters * 0.000621371;
  return `${miles.toFixed(1)} mi`;
}

/** Format durationSeconds into a human-readable string. */
export function formatDuration(seconds: number): string {
  if (seconds < 3600) {
    return `${Math.round(seconds / 60)} min`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `${h} hr ${m} min` : `${h} hr`;
}

/** Return the currency symbol for an org's default currency. */
export function currencySymbol(currency?: OrgCurrency): string {
  if (currency === 'EUR') return '€';
  if (currency === 'GBP') return '£';
  return '$';
}

/** Format an ISO date string (YYYY-MM-DD or YYYY-MM-DDTHH:mm) for display.
 *  Shows time only when the value includes a time component. */
export function formatMoveDate(iso: string): string {
  // Handle YYYY-MM-DD by appending T00:00 to avoid timezone shift
  const hasTime = iso.includes('T');
  const d = hasTime ? new Date(iso) : new Date(`${iso}T00:00`);
  const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (!hasTime) return dateStr;
  const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${dateStr} at ${timeStr}`;
}
